// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import {
  fetchCoinbaseAccounts,
  fetchCoinbaseTransactions,
  fetchCoinbaseBuyDetails,
  fetchCoinbaseSellDetails,
} from '../../services/coinbase-api.js';
import type { CoinbaseAccount, CoinbaseBuySellDetails, CoinbaseApiCredential } from '../../services/coinbase-api.js';
import { mapCoinbaseTransactions } from '../../services/coinbase-mapper.js';
import type { MappingContext } from '../../services/coinbase-mapper.js';
import { detectDuplicates } from '../../lib/import/duplicate-detector.js';
import type { ImportedTransaction, ReviewedTransaction } from '../../lib/import/types.js';
import { buildTransactionPayload, findImbalanceAccount } from '../../lib/import/pipeline.js';
import { buildEvent, validateSplitsBalance } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { ImportReviewTable } from './ImportReviewTable.js';

interface Props {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

type Step = 'accounts' | 'fetching' | 'review' | 'done';

export function CoinbaseImport({ worker, keyData }: Props) {
  const { t } = useTranslation(['import', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const credential = state.appSettings.coinbaseCredential;
  const storedMapping = state.appSettings.coinbaseAccountMapping;
  const jwtToken = state.auth.token ?? '';

  const ledgerAccounts = [...ledger.accounts.values()].filter((a) => !a.hidden && !a.placeholder);

  const [step, setStep] = useState<Step>('accounts');
  const [coinbaseAccounts, setCoinbaseAccounts] = useState<CoinbaseAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [accountMappings, setAccountMappings] = useState<Record<string, string>>({});
  const [fetchProgress, setFetchProgress] = useState('');
  const [reviewed, setReviewed] = useState<ReviewedTransaction[]>([]);
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!credential) return;
    setLoadingAccounts(true);
    setError('');
    const apiCred: CoinbaseApiCredential = {
      apiKey: credential.apiKey,
      apiSecret: credential.apiSecret,
      jwtToken,
    };
    fetchCoinbaseAccounts(apiCred)
      .then((accounts) => {
        const nonEmpty = accounts.filter(
          (a) => parseFloat(a.balance.amount) !== 0 || a.type === 'fiat',
        );
        setCoinbaseAccounts(nonEmpty.length > 0 ? nonEmpty : accounts);
        const init: Record<string, string> = {};
        for (const acc of accounts) {
          const mapped = storedMapping[acc.id];
          if (mapped) init[acc.id] = mapped;
        }
        setAccountMappings(init);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : t('coinbase.fetchAccountsFailed'));
      })
      .finally(() => setLoadingAccounts(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canFetch =
    selectedIds.size > 0 && [...selectedIds].every((id) => Boolean(accountMappings[id]));

  const handleFetch = async () => {
    if (!credential) return;
    setStep('fetching');
    setError('');

    const apiCred: CoinbaseApiCredential = {
      apiKey: credential.apiKey,
      apiSecret: credential.apiSecret,
      jwtToken,
    };
    const context: MappingContext = {
      accounts: ledgerAccounts,
      coinbaseAccounts,
      coinbaseAccountMapping: accountMappings,
      defaultCommissionAccount: state.appSettings.defaultCommissionAccount,
      homeCurrency: state.appSettings.homeCurrency,
    };

    try {
      const allImported: ImportedTransaction[] = [];
      const ids = [...selectedIds];

      for (let i = 0; i < ids.length; i++) {
        const cbAccountId = ids[i]!;
        const cbAccount = coinbaseAccounts.find((a) => a.id === cbAccountId);
        setFetchProgress(
          t('coinbase.fetching', { name: cbAccount?.name ?? cbAccountId, current: i + 1, total: ids.length }),
        );

        const txs = await fetchCoinbaseTransactions(apiCred, cbAccountId);

        setFetchProgress(t('coinbase.loadingDetails', { name: cbAccount?.name ?? cbAccountId }));
        const buyDetailsMap = new Map<string, CoinbaseBuySellDetails>();
        const sellDetailsMap = new Map<string, CoinbaseBuySellDetails>();

        for (const tx of txs) {
          if (tx.buy?.id) {
            try {
              buyDetailsMap.set(
                tx.buy.id,
                await fetchCoinbaseBuyDetails(apiCred, cbAccountId, tx.buy.id),
              );
            } catch { /* skip — mapper falls back to simple import */ }
          }
          if (tx.sell?.id) {
            try {
              sellDetailsMap.set(
                tx.sell.id,
                await fetchCoinbaseSellDetails(apiCred, cbAccountId, tx.sell.id),
              );
            } catch { /* skip */ }
          }
        }

        const results = mapCoinbaseTransactions(
          txs,
          buyDetailsMap,
          sellDetailsMap,
          cbAccountId,
          context,
        );
        for (const r of results) {
          if (r.transaction) allImported.push(r.transaction);
        }
      }

      setReviewed(detectDuplicates(allImported, ledger));
      setStep('review');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('coinbase.fetchTransactionsFailed'));
      setStep('accounts');
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');
    const sessionId = uuidv4();
    let imbalanceAccountId =
      findImbalanceAccount(ledger, state.appSettings.homeCurrency) ?? '';
    if (!imbalanceAccountId) {
      const expense = [...ledger.accounts.values()].find((a) => a.type === 'EXPENSE');
      imbalanceAccountId = expense?.id ?? '';
    }

    const toImport = reviewed.filter((t) => t.selected);
    const nextSeq = ledger.lastSequence + 1;
    let count = 0;

    for (let i = 0; i < toImport.length; i++) {
      const tx = toImport[i]!;
      try {
        const payload = buildTransactionPayload(tx, sessionId, ledger, imbalanceAccountId);
        validateSplitsBalance(payload.splits);
        const event = await buildEvent('TransactionPosted', payload, nextSeq + i);
        await encryptAndPush(event);
        count++;
        setImportedCount(count);
      } catch (err: unknown) {
        setError(
          t('errorOnTransaction', { index: i + 1, message: err instanceof Error ? err.message : String(err) }),
        );
        setImporting(false);
        return;
      }
    }

    setImporting(false);
    setStep('done');
  };

  if (!credential) {
    return (
      <div className="py-12 text-center space-y-3">
        <p className="text-text-muted text-sm">{t('coinbase.noAccount')}</p>
        <a href="/settings" className="text-accent hover:underline text-sm">
          {t('coinbase.connectInSettings')}
        </a>
      </div>
    );
  }

  if (step === 'accounts') {
    return (
      <div className="space-y-5">
        {loadingAccounts ? (
          <p className="text-text-muted text-sm animate-pulse">{t('coinbase.loadingAccounts')}</p>
        ) : coinbaseAccounts.length === 0 ? (
          <p className="text-text-muted text-sm">{t('coinbase.noAccountsWithBalances')}</p>
        ) : (
          <>
            <p className="text-sm text-text-muted">
              {t('coinbase.selectPrompt')}
            </p>

            <div className="border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-bg-elevated border-b border-border text-xs text-text-muted uppercase tracking-wide">
                  <tr>
                    <th className="w-8 px-3 py-2" />
                    <th className="px-3 py-2 text-left">{t('coinbase.account')}</th>
                    <th className="px-3 py-2 text-right">{t('coinbase.balance')}</th>
                    <th className="px-3 py-2 text-left">{t('coinbase.importToAccount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {coinbaseAccounts.map((acc) => (
                    <tr key={acc.id} className="bg-bg hover:bg-bg-elevated">
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(acc.id)}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(acc.id);
                            else next.delete(acc.id);
                            setSelectedIds(next);
                          }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-text">{acc.name}</div>
                        <div className="text-xs text-text-muted font-mono">{acc.currency.code}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-text">
                        {acc.balance.amount} {acc.balance.currency}
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={accountMappings[acc.id] ?? ''}
                          onChange={(e) =>
                            setAccountMappings((m) => ({ ...m, [acc.id]: e.target.value }))
                          }
                          disabled={!selectedIds.has(acc.id)}
                          className="w-full bg-bg border border-border text-text text-xs px-2 py-1 focus:border-accent focus:outline-none disabled:opacity-40"
                        >
                          <option value="">{t('selectAccount')}</option>
                          {ledgerAccounts.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {error && <p className="text-negative text-sm">{error}</p>}

            <div className="flex items-center gap-4">
              <button
                onClick={() => { void handleFetch(); }}
                disabled={!canFetch}
                className="px-4 py-2 bg-accent text-white text-sm hover:bg-accent-hover disabled:opacity-50 transition-colors"
              >
                {t('coinbase.fetchTransactions')}
              </button>
              {!canFetch && selectedIds.size > 0 && (
                <p className="text-xs text-text-muted">
                  {t('coinbase.assignPrompt')}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  if (step === 'fetching') {
    return (
      <div className="py-16 text-center space-y-3">
        <p className="text-text-muted text-sm animate-pulse">
          {fetchProgress || t('coinbase.fetchingDefault')}
        </p>
      </div>
    );
  }

  if (step === 'review') {
    const toImport = reviewed.filter((t) => t.selected);
    return (
      <div className="space-y-4">
        <ImportReviewTable transactions={reviewed} accounts={ledgerAccounts} onChange={setReviewed} />
        {error && <p className="text-negative text-sm">{error}</p>}
        <div className="flex gap-3">
          <button
            onClick={() => setStep('accounts')}
            className="px-4 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors"
          >
            {t('common:actions.back')}
          </button>
          <button
            onClick={() => { void handleImport(); }}
            disabled={toImport.length === 0 || importing}
            className="px-4 py-2 bg-accent text-white text-sm hover:bg-accent-hover disabled:opacity-50 transition-colors"
          >
            {importing
              ? t('importingProgress', { current: importedCount, total: toImport.length })
              : t('importCount', { count: toImport.length })}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="text-center space-y-4 py-8">
        <p className="font-mono text-positive text-2xl">✓</p>
        <p className="text-md font-medium text-text">{t('importedCount', { count: importedCount })}</p>
        <button
          onClick={() => {
            setStep('accounts');
            setReviewed([]);
            setSelectedIds(new Set());
            setImportedCount(0);
          }}
          className="px-4 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors"
        >
          {t('coinbase.importMore')}
        </button>
      </div>
    );
  }

  return null;
}
